/* ============================================================
   DATA PELAYANAN KESEHATAN

   Letakkan di: app/layanan/pelayanan/pelayanan.js
   Fotonya di:  app/layanan/pelayanan/images/

   Alamat halamannya /layanan/pelayanan - sudah ditunjuk menu
   Header, dan sebelum berkas ini ada, menu itu jatuh ke halaman
   404.

   Isinya disalin dari halaman "Pelayanan Kesehatan RS JMC" di situs
   lama beserta kaki halamannya.

   ============================================================
   ⚠️⚠️ SATU HAL YANG HARUS DIPUTUSKAN SEBELUM HALAMAN INI TAYANG

   NOMOR BPJS BERBEDA ANTAR SUMBER. Ini bukan soal kerapian data -
   pasien BPJS yang menelepon nomor yang salah kehilangan waktu di
   saat ia justru sedang butuh kepastian.

   Yang saya temukan:

     0812-1208-777   tertulis "BPJS" di kaki halaman situs lama
                     DAN di kaki sebagian besar poster poliklinik
                     (Bedah, Paru, Gigi, Anak, Saraf, THT, Mata,
                     Kulit, Ortopedi, Kandungan, Penyakit Dalam)

     0812 1208 777  tertulis "BPJS" di kaki poster Poliklinik
                     Urologi, DAN dipakai sebagai nomor BPJS/JKN di
                     app/jadwal-dokter/dokter.js

   Keduanya tidak mungkin sama-sama benar sebagai satu-satunya
   nomor BPJS. Kemungkinannya: salah satu sudah tidak dipakai, atau
   keduanya aktif untuk keperluan berbeda.

   ⚠️ MASALAH KEDUA, DAN INI LEBIH MENGKHAWATIRKAN:

   0812-1208-777 JUGA dipakai sebagai nomor Customer Service JMC
   Skincare - lihat app/layanan/jmc-skincare/jmc-skincare.js, yang
   diambil dari poster JMC Skincare.

   Satu nomor tidak lazim melayani BPJS sekaligus klinik estetika.
   Salah satu label itu hampir pasti keliru.

   → SAMPAI DIPASTIKAN, nomor BPJS di bawah saya beri tanda
     `perluDicek: true`. Halaman TETAP menampilkannya - menyembunyikan
     nomor BPJS sama sekali lebih merugikan daripada menampilkan
     nomor yang mungkin perlu dikoreksi - tapi lihat catatan di
     PelayananContent.jsx soal cara menampilkannya.

   Setelah dipastikan, hapus `perluDicek` dan samakan juga nomornya
   di app/jadwal-dokter/dokter.js supaya seluruh situs konsisten.

   ============================================================
   ⚠️ BUG YANG DITEMUKAN DI BERKAS LAIN

   app/jadwal-dokter/dokter.js baris KOMPLAIN menulis:

       { tampil: "0815 801 7000", tel: "+62815801700", ... }

   Medan `tel` kurang satu angka nol di ujungnya. Seharusnya
   "+628158017000". Tombol telepon di halaman jadwal dokter
   menghubungi nomor yang tidak lengkap.

   Tidak saya perbaiki dari sini karena berkasnya di luar folder
   ini - perbaiki langsung di sana.
   ============================================================ */

/* ============================================================
   NOMOR TELEPON

   Urutannya sengaja: yang paling sering dibutuhkan di atas.

   `utama` menentukan mana yang jadi tombol besar. Hanya satu.

   `wa` diisi hanya untuk nomor yang memang melayani WhatsApp.
   Nomor hunting (021) adalah nomor kantor - tidak ada WhatsApp di
   sana, dan tombol WhatsApp yang tidak dijawab lebih membuat orang
   enggan daripada tidak ada tombolnya.
   ============================================================ */
export const NOMOR = [
  {
    kunci: "cs",
    label: { id: "Customer Service", en: "Customer Service" },
    keterangan: {
      id: "Informasi layanan, jadwal dokter, dan pendaftaran umum.",
      en: "Service information, doctor schedules and general registration.",
    },
    tampil: "0811-901-874",
    tel: "+62811901874",
    wa: "62811901874",
    utama: true,
  },
  {
    kunci: "hunting",
    label: { id: "Hunting", en: "Main line" },
    keterangan: {
      id: "Nomor utama rumah sakit. Sambungan akan dialihkan ke bagian yang dituju.",
      en: "The hospital's main line. Your call will be routed to the relevant department.",
    },
    tampil: "(021) 798 0888",
    tel: "+62217980888",
  },
  {
    kunci: "asuransi",
    label: { id: "Asuransi / Umum", en: "Insurance / Private" },
    keterangan: {
      id: "Pertanyaan seputar penjaminan asuransi dan pasien umum.",
      en: "Questions about insurance coverage and private patients.",
    },
    tampil: "0813-1800-2300",
    tel: "+6281318002300",
    wa: "6281318002300",
  },
  {
    kunci: "bpjs",
    label: { id: "BPJS", en: "BPJS" },
    keterangan: {
      id: "Pendaftaran dan pertanyaan untuk peserta BPJS Kesehatan.",
      en: "Registration and enquiries for BPJS Kesehatan members.",
    },
    tampil: "0812-1208-777",
    tel: "+628121208777",
    wa: "628121208777",
    /* ⚠️ Lihat catatan panjang di kepala berkas. Nomor ini berbeda
       dengan yang tertulis di dokter.js dan poster Urologi, dan
       bertabrakan dengan nomor CS JMC Skincare. HAPUS TANDA INI
       setelah bagian pendaftaran memastikan mana yang benar. */
    perluDicek: true,
  },
  {
    kunci: "komplain",
    label: { id: "Pengaduan", en: "Complaints" },
    keterangan: {
      id: "Untuk menyampaikan keluhan atau masukan atas pelayanan.",
      en: "To report a complaint or give feedback about our service.",
    },
    tampil: "0815-801-7000",
    tel: "+628158017000",
    wa: "628158017000",
  },
];

/* ============================================================
   ALAMAT DAN SUREL

   Disalin dari kaki halaman situs lama. Alamatnya sama persis
   dengan yang dipakai halaman IGD dan Laboratorium - kalau suatu
   saat berubah, ketiganya harus ikut diubah.

   ⚠️ Surelnya beralamat gmail.com, bukan domain rumah sakit.
   Bukan kesalahan penyalinan - memang begitu tertulis di situs
   lama. Tapi layak diusulkan ke bagian IT: alamat surel berdomain
   sendiri lebih dipercaya, dan lebih tahan kalau pemegang akunnya
   berganti.
   ============================================================ */
export const ALAMAT = {
  baris:
    "Jl. Warung Buncit Raya No. 15, RT 10/RW 05, Kalibata, Kec. Pancoran, Jakarta Selatan, DKI Jakarta 12740",
  /* Pencarian berdasarkan nama dan alamat, bukan koordinat - kalau
     titiknya digeser di Google Maps, tautannya tetap benar. */
  cari: "Rumah Sakit Jakarta Medical Center, Jl. Warung Buncit Raya No. 15, Jakarta Selatan",
};

export const SUREL = "rsjmcid@rsjmc.id";

/* ============================================================
   PESAN PEMBUKA WHATSAPP

   Terisi otomatis di kolom ketik saat nomor ber-WhatsApp ditekan.
   WhatsApp hanya MENGISI, tidak mengirim - pasien masih bisa
   menyunting sebelum menekan kirim.

   Sengaja umum, karena halaman ini memuat lima nomor untuk lima
   keperluan berbeda. Nomor yang punya pesan sendiri bisa menimpanya
   lewat medan `pesanWa` di larik NOMOR di atas.
   ============================================================ */
export const PESAN_WA = {
  id: "Halo RS Jakarta Medical Center,\n\nSaya ingin menanyakan informasi mengenai layanan kesehatan di RS JMC.\n\nMohon bantuannya. Terima kasih.",
  en: "Hello RS Jakarta Medical Center,\n\nI would like to ask about the health services at RS JMC.\n\nThank you for your help.",
};

/* ============================================================
   FOTO FASILITAS

   Diimpor, bukan ditaruh di public/ - Next.js membaca ukurannya
   saat build, jadi tiap foto disajikan dalam ukuran yang pas dan
   dapat efek blur saat dimuat.

   ⚠️ KETERANGANNYA MENYEBUTKAN APA YANG TERLIHAT DI FOTO, TITIK.
   Tidak ada "nyaman", "modern", atau "bersih" - itu penilaian, dan
   penilaian di keterangan foto rumah sakit terbaca sebagai iklan.
   Biarkan fotonya sendiri yang bicara.

   ⚠️ Foto ketiga memuat beberapa orang di ruang tunggu, wajahnya
   tidak jelas tapi tetap terlihat. Untuk foto di area rumah sakit,
   pastikan bagian humas sudah menimbang ini - orang yang sedang
   berobat tidak selalu ingin terlihat sedang berobat.
   ============================================================ */
import fotoPendaftaran from "./images/pendaftaran.jpg";
import fotoApotek from "./images/apotek-lobi.jpg";
import fotoRuangTunggu from "./images/ruang-tunggu.jpg";

export const FOTO = [
  {
    gambar: fotoPendaftaran,
    judul: { id: "Loket Pendaftaran", en: "Registration Desk" },
    alt: {
      id: "Loket pendaftaran RS Jakarta Medical Center dengan deretan meja petugas dan kursi tunggu.",
      en: "The registration desk at Jakarta Medical Center Hospital, with staff counters and waiting chairs.",
    },
  },
  {
    gambar: fotoApotek,
    judul: { id: "Apotek dan Lobi", en: "Pharmacy and Lobby" },
    alt: {
      id: "Area apotek dan lobi RS Jakarta Medical Center, dengan lemari obat dan meja pelayanan.",
      en: "The pharmacy and lobby area at Jakarta Medical Center Hospital, with medicine cabinets and service counters.",
    },
  },
  {
    gambar: fotoRuangTunggu,
    judul: { id: "Ruang Tunggu Farmasi", en: "Pharmacy Waiting Area" },
    alt: {
      id: "Ruang tunggu di depan loket farmasi RS Jakarta Medical Center, dengan layar antrean di atasnya.",
      en: "The waiting area in front of the pharmacy counter at Jakarta Medical Center Hospital, with a queue display above.",
    },
  },
];
